import { ClosePaneButton } from "@components/ClosePaneButton";
import { PatientClientName } from "@components/PatientClientName";
import { faClock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FolderTab } from "@scribenote/brian-design-system";
import { kindLabel, type Subject } from "@subjects/subjectsClient";
import { SubjectWorkspace } from "@subjects/SubjectWorkspace";
import { formatMonthDay, formatTime } from "@utils/format-date";
import { isOnTodayBoard, type TodayVisit } from "@visits/visitsClient";

/**
 * The single subject view in the workspace main content, reached however a subject
 * is selected — a patient-flow card, a room's occupant, or a search result. The
 * record as an opened manila folder: the patient is written on the tab, and the
 * folder holds the index rail and its reader (WEB-3288).
 *
 * Renders any subject kind: from search it may be a client / user / room, in which
 * case there are no estimates and the tab's small print falls back to the subject's
 * kind. For a patient with a visit today, `visit` enriches it with the reason and
 * scheduled time. `onClear` returns to the rooms grid.
 *
 * The window is the corkboard (CompanionShell owns that surface), so this wrapper is
 * padding only: the folder lies directly on the cork with a gutter all round, an
 * object laid on the board rather than a panel bolted to the window. Inside, the
 * folder body runs full bleed: the rail and the reader own their own padding and
 * scroll regions.
 */
export const SubjectPane = ({
  subject,
  visit,
  onClear,
}: {
  subject: Subject;
  /** Present when the subject is a patient with a resolvable visit — today's, or
   * one still open from an earlier day (WEB-3271). */
  visit?: TodayVisit;
  onClear: () => void;
}) => {
  // The owner's name, shown muted after a patient's name. A visit carries it
  // for today's patients; a subject opened from search carries its own.
  const clientName =
    subject.kind === "patient"
      ? (visit?.clientName ?? subject.clientName)
      : undefined;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
      <FolderTab
        fill
        className="min-h-0 flex-1"
        tab={
          // The tab is the record's title. FolderTab writes it into a span, so
          // the heading role is spelled out here to keep the pane in the
          // document outline.
          <span role="heading" aria-level={2}>
            <PatientClientName
              patientName={subject.name}
              clientName={clientName}
              clientClassName="font-normal text-annotation"
            />
          </span>
        }
        tabMeta={
          visit ? (
            <span className="flex items-center gap-2">
              <span className="truncate">
                {visit.reason ?? visit.visitType}
              </span>
              {visit.scheduledStart && (
                <span className="flex shrink-0 items-center gap-1.5 font-mono text-fine-print">
                  <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                  {isOnTodayBoard(visit.scheduledStart)
                    ? formatTime(visit.scheduledStart)
                    : `${formatMonthDay(visit.scheduledStart)}, ${formatTime(visit.scheduledStart)}`}
                </span>
              )}
            </span>
          ) : (
            <span className="capitalize">{kindLabel(subject)}</span>
          )
        }
        actions={<ClosePaneButton onClick={onClear} />}
      >
        {/* Keyed on the subject, so another patient starts fresh. */}
        <SubjectWorkspace
          key={subject.id}
          subjectId={subject.id}
          kind={subject.kind}
          visitId={visit?.id ?? null}
          roomUuid={visit?.currentRoomUuid ?? null}
        />
      </FolderTab>
    </div>
  );
};
